/**
 * Confusion Matrix Panel (2026-05-05, v2-aware refactor 2026-05-08)
 *
 * Renders the SET PROFILE FIRST backtest in a tabular admin view.
 * Rows are derived from the actual `per_profile_type` keys returned by
 * the backend so v2 labels (STOMP_CLEAN/CLEAN, CONTROLLED_LONG/CLEAN,
 * BALANCED_LONG/CLEAN, TRUE_CHAOS/CLEAN, …) and the `MISSING_PROFILE_TYPE`
 * bucket are surfaced automatically — no hardcoded label list.
 *
 * Pure presentational — fetches `/api/tt/admin/set-profile-engine-backtest`
 * once per `days` change. Audit-only.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { adminGet } from '../data/api';
import { Grid3x3, RefreshCcw, AlertTriangle } from 'lucide-react';

const CLUSTERS = ['3-0', '3-1', '3-2', '0-3', '1-3', '2-3'];

const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT'));

// Heatmap color: 0 → zinc-900, max → emerald-700
const cellColor = (count, max) => {
  if (!max || count === 0) return 'bg-zinc-900/40 text-zinc-600';
  const ratio = count / max;
  if (ratio < 0.2) return 'bg-emerald-950/40 text-emerald-300';
  if (ratio < 0.4) return 'bg-emerald-900/40 text-emerald-200';
  if (ratio < 0.7) return 'bg-emerald-800/50 text-emerald-100';
  return 'bg-emerald-700/60 text-emerald-50';
};

const versionBadge = (v) => {
  switch (v) {
    case 'v2':
      return ['v2', 'bg-emerald-900/50 text-emerald-200 border-emerald-700'];
    case 'v1':
      return ['v1 (legacy)', 'bg-amber-900/50 text-amber-200 border-amber-700'];
    case 'mixed':
      return ['v1+v2 mixed', 'bg-violet-900/50 text-violet-200 border-violet-700'];
    default:
      return ['unknown', 'bg-zinc-800/60 text-zinc-300 border-zinc-700'];
  }
};

export default function ConfusionMatrixPanel({ pin, defaultDays = 30 }) {
  const [days, setDays] = useState(defaultDays);
  const [versionFilter, setVersionFilter] = useState('all');
  const [sinceDate, setSinceDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showLowSample, setShowLowSample] = useState(false);

  const load = async (
    d = days,
    vf = versionFilter,
    sd = sinceDate,
  ) => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      params.set('days', String(d));
      if (vf && vf !== 'all') params.set('version_filter', vf);
      if (sd) params.set('since_date', sd);
      const r = await adminGet(
        `/tt/admin/set-profile-engine-backtest?${params.toString()}`,
        pin,
      );
      setData(r);
    } catch (e) {
      setErr(e?.message || 'backtest load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derive row order from the actual keys returned by the backend, sorted
  // by sample size desc so the dominant profile shows first. We never
  // hardcode a label list — v2 keys, v1 legacy keys, and the
  // MISSING_PROFILE_TYPE bucket all flow through automatically.
  const profileRows = useMemo(() => {
    const cm = data?.confusion_matrix || {};
    const stats = data?.per_profile_type || {};
    const allKeys = new Set([...Object.keys(cm), ...Object.keys(stats)]);
    return Array.from(allKeys)
      .map((k) => ({
        key: k,
        row: cm[k] || {},
        stats: stats[k] || {},
        n: stats[k]?.n ?? 0,
        settled: stats[k]?.settled ?? 0,
      }))
      .sort((a, b) => b.n - a.n || a.key.localeCompare(b.key));
  }, [data]);

  const rowMax = (row) =>
    Math.max(...CLUSTERS.map((c) => row?.[c] || 0), 0);

  const summary = data?.summary || {};
  const [versionLabel, versionCls] = versionBadge(summary.engine_version);

  const visibleCombos = (data?.combos?.best || []).filter(
    (c) => showLowSample || !c.low_sample,
  );

  return (
    <div
      data-testid="confusion-matrix-panel"
      className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-3 text-[11px] space-y-3"
      style={{ fontFamily: 'monospace' }}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Grid3x3 size={13} className="text-violet-400" />
          <span className="text-zinc-100 font-semibold tracking-wide">
            SET PROFILE BACKTEST
          </span>
          {data && (
            <span
              data-testid="engine-version-badge"
              className={`text-[9px] px-1.5 py-0.5 border rounded-sm ${versionCls}`}
            >
              {versionLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="confusion-version-select"
            value={versionFilter}
            onChange={(e) => {
              const v = e.target.value;
              setVersionFilter(v);
              load(days, v, sinceDate);
            }}
            className="bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-0.5 text-zinc-100 text-[10px]"
            title="Filter analyses by classifier version. v2_only excludes legacy v1 labels (CHAOS/FAST/LONG/STOMP) which are not in the v2 cluster compatibility map."
          >
            <option value="all">all versions</option>
            <option value="v2_only">v2 only</option>
            <option value="v1_only">v1 only (legacy)</option>
            <option value="since_shadow">since shadow deploy</option>
          </select>
          <input
            type="date"
            data-testid="confusion-since-date"
            value={sinceDate}
            onChange={(e) => {
              const v = e.target.value;
              setSinceDate(v);
              load(days, versionFilter, v);
            }}
            className="bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-0.5 text-zinc-100 text-[10px]"
            title="Custom cut-off date — overrides the days window when set. Clear to revert."
          />
          <select
            data-testid="confusion-days-select"
            value={days}
            onChange={(e) => {
              const d = Number(e.target.value);
              setDays(d);
              load(d, versionFilter, sinceDate);
            }}
            className="bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-0.5 text-zinc-100 text-[10px]"
          >
            {[7, 14, 30, 60, 90, 180].map((d) => (
              <option key={d} value={d}>{`${d}d`}</option>
            ))}
          </select>
          <button
            data-testid="confusion-reload-btn"
            onClick={() => load(days, versionFilter, sinceDate)}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
          >
            <RefreshCcw size={11} className={loading ? 'animate-spin' : ''} />
            {loading ? 'loading' : 'reload'}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-rose-300 px-2 py-1 rounded-sm bg-rose-950/40 border border-rose-800">
          {err}
        </div>
      )}

      {data && (
        <>
          {/* Aggregate KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <KPI label="ANALYZED" value={fmtInt(summary.analyzed)} testid="kpi-analyzed" />
            <KPI label="SETTLED" value={fmtInt(summary.settled)} testid="kpi-settled" />
            <KPI
              label="HIT STRICT"
              value={fmtPct(data.hit_rate_strict_pct)}
              testid="confusion-hit-strict"
            />
            <KPI
              label="HIT COMPATIBLE"
              value={fmtPct(data.hit_rate_compatible_pct)}
              testid="confusion-hit-compatible"
            />
          </div>

          {/* Diagnostic block (v2 awareness) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <KPI
              label="WITH PROFILE"
              value={fmtInt(summary.with_profile_type)}
              testid="kpi-with-profile"
            />
            <KPI
              label="MISSING PROFILE"
              value={fmtInt(summary.missing_profile_type)}
              tone={summary.missing_profile_type ? 'amber' : 'zinc'}
              testid="kpi-missing-profile"
            />
            <KPI
              label="V2 LABELS"
              value={fmtInt(summary.v2_profile_labels_count)}
              tone="emerald"
              testid="kpi-v2-labels"
            />
            <KPI
              label="V1 (LEGACY)"
              value={fmtInt(summary.old_profile_labels_count)}
              tone={summary.old_profile_labels_count ? 'amber' : 'zinc'}
              testid="kpi-v1-labels"
            />
          </div>

          {data.filters && (
            <div
              data-testid="confusion-filters-summary"
              className="text-[10px] text-zinc-500 flex flex-wrap items-center gap-2"
            >
              <span>filters:</span>
              <span
                className={`px-1.5 py-0.5 rounded-sm text-[9px] tracking-wider ${
                  data.filters.version_filter === 'v2_only'
                    ? 'bg-emerald-900/40 text-emerald-200'
                    : data.filters.version_filter === 'v1_only'
                    ? 'bg-amber-900/40 text-amber-200'
                    : data.filters.version_filter === 'since_shadow'
                    ? 'bg-violet-900/40 text-violet-200'
                    : 'bg-zinc-800/60 text-zinc-300'
                }`}
                data-testid="confusion-filter-version"
              >
                {data.filters.version_filter}
              </span>
              <span className="text-zinc-600">
                cutoff {String(data.filters.cutoff).slice(0, 10)}{' '}
                ({data.filters.cutoff_source})
              </span>
              {data.filters.excluded_by_version_filter > 0 && (
                <span
                  className="text-zinc-600"
                  title="Rows excluded by the version_filter (e.g. v2_only drops v1 legacy rows)."
                >
                  · excluded {data.filters.excluded_by_version_filter}
                </span>
              )}
              {data.n_settled_v2_only != null && (
                <span
                  className="text-zinc-600"
                  title="Settled v2 rows used as denominator for hit-rate metrics (v1 legacy rows excluded because v1 labels are not in the v2 cluster compatibility map)."
                >
                  · n_settled_v2_only={data.n_settled_v2_only}
                </span>
              )}
            </div>
          )}

          {summary.engine_version === 'mixed' && (
            <div className="flex items-start gap-1.5 text-[10px] text-amber-200 bg-amber-950/40 border border-amber-800 px-2 py-1 rounded-sm">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              <span>
                Mixed v1+v2 labels present in window. Hit-rate metrics are
                computed across both; consider tightening the `days` filter
                to isolate the v2 era for a cleaner read.
              </span>
            </div>
          )}

          {data.distribution_warning && (
            <div className="flex items-start gap-1.5 text-[10px] text-rose-200 bg-rose-950/40 border border-rose-800 px-2 py-1 rounded-sm"
                 data-testid="distribution-warning">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              <span>{data.distribution_warning}</span>
            </div>
          )}

          {/* Confusion matrix table */}
          <div>
            <div className="text-[10px] text-zinc-500 mb-1 tracking-wider">
              CONFUSION: predicted profile (rows) → actual cluster (cols)
              {profileRows.length > 0 && (
                <span className="ml-2 text-zinc-600">
                  · {profileRows.length} observed profile bucket(s)
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left py-1 pr-2">profile</th>
                    {CLUSTERS.map((c) => (
                      <th key={c} className="text-center w-12">
                        {c}
                      </th>
                    ))}
                    <th className="text-right pl-2">n</th>
                    <th className="text-right pl-2">settled</th>
                    <th className="text-right pl-2">strict</th>
                    <th className="text-right pl-2">compat</th>
                  </tr>
                </thead>
                <tbody>
                  {profileRows.length === 0 && (
                    <tr>
                      <td colSpan={CLUSTERS.length + 4}
                          className="text-center text-zinc-600 py-3">
                        no profiled matches in window
                      </td>
                    </tr>
                  )}
                  {profileRows.map(({ key, row, stats }) => {
                    const maxC = rowMax(row);
                    const isMissing = key.startsWith('MISSING_PROFILE_TYPE');
                    const isLegacyV1 = !!stats?.legacy_v1;
                    return (
                      <tr
                        key={key}
                        data-testid={`confusion-row-${key}`}
                        className={isLegacyV1 ? 'opacity-60' : ''}
                      >
                        <td
                          className={`font-semibold py-1 pr-2 ${
                            isMissing ? 'text-amber-300'
                              : isLegacyV1 ? 'text-amber-200'
                              : 'text-zinc-200'
                          }`}
                        >
                          {key}
                          {isLegacyV1 && (
                            <span
                              className="ml-1 px-1 py-0 rounded-sm text-[8px] bg-amber-900/40 text-amber-200 align-middle"
                              data-testid={`legacy-v1-badge-${key}`}
                              title="Legacy v1 profile label. Not present in the v2 cluster compatibility map — strict/compat metrics are intentionally null (showing 0% would be misleading)."
                            >
                              legacy_v1
                            </span>
                          )}
                        </td>
                        {CLUSTERS.map((c) => {
                          const count = row[c] || 0;
                          return (
                            <td
                              key={c}
                              className={`text-center py-0.5 ${cellColor(
                                count,
                                maxC,
                              )}`}
                            >
                              {count || ''}
                            </td>
                          );
                        })}
                        <td className="text-right text-zinc-400 pl-2">
                          {stats.n ?? 0}
                        </td>
                        <td className="text-right text-zinc-300 pl-2">
                          {stats.settled ?? 0}
                        </td>
                        <td className="text-right text-emerald-300 pl-2">
                          {(isMissing || isLegacyV1) ? 'n/a'
                            : fmtPct(stats.accuracy_strict_pct)}
                        </td>
                        <td className="text-right text-violet-300 pl-2">
                          {(isMissing || isLegacyV1) ? 'n/a'
                            : fmtPct(stats.accuracy_compatible_pct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* MW avoidance + per-market */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 border-t border-zinc-800">
            <div>
              <div className="text-[10px] text-zinc-500 mb-1 tracking-wider">
                MW AVOIDANCE — engine recommended a non-MW market
              </div>
              <div className="text-zinc-200">
                <span className="text-emerald-300 font-bold">
                  {data.mw_avoidance?.n ?? 0}
                </span>{' '}
                cases switched away from MW
              </div>
              {data.mw_avoidance?.switched_to &&
                Object.keys(data.mw_avoidance.switched_to).length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {Object.entries(data.mw_avoidance.switched_to).map(
                      ([k, v]) => (
                        <li key={k} className="text-zinc-400 text-[10px]">
                          → <b className="text-zinc-200">{k}</b>: {v}
                        </li>
                      ),
                    )}
                  </ul>
                )}
            </div>
            <div>
              <div className="text-[10px] text-zinc-500 mb-1 tracking-wider">
                PER RECOMMENDED MARKET
              </div>
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left">market</th>
                    <th className="text-right">n</th>
                    <th className="text-right">settled</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.per_recommended_market || {}).map(
                    ([k, v]) => (
                      <tr key={k} className="text-zinc-300">
                        <td>{k}</td>
                        <td className="text-right">{v.n}</td>
                        <td className="text-right">{v.settled}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Best combos */}
          {(data.combos?.best?.length || 0) > 0 && (
            <div className="pt-1 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] text-zinc-500 tracking-wider">
                  BEST COMBOS · sorted by compatible accuracy
                  <span className="ml-1 text-zinc-600">
                    (low-sample = settled &lt; {data.combos.min_combo_n ?? 3})
                  </span>
                </div>
                <label className="flex items-center gap-1 text-[10px] text-zinc-400 cursor-pointer">
                  <input
                    data-testid="combos-show-low-sample"
                    type="checkbox"
                    checked={showLowSample}
                    onChange={(e) => setShowLowSample(e.target.checked)}
                    className="accent-violet-500"
                  />
                  show low-sample
                </label>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="text-zinc-500">
                      <th className="text-left">profile</th>
                      <th className="text-left">fatigue</th>
                      <th className="text-left">market</th>
                      <th className="text-right">n</th>
                      <th className="text-right">settled</th>
                      <th className="text-right">strict</th>
                      <th className="text-right">compat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCombos.length === 0 && (
                      <tr>
                        <td colSpan={7}
                            className="text-center text-zinc-600 py-2">
                          no combo with sufficient sample size — toggle
                          “show low-sample” to inspect early signal
                        </td>
                      </tr>
                    )}
                    {visibleCombos.slice(0, 10).map((c, i) => (
                      <tr
                        key={`${c.profile_type}-${c.fatigue_type}-${c.recommended_market}-${i}`}
                        data-testid={`combo-best-${i}`}
                        className={c.low_sample
                          ? 'text-zinc-500 opacity-70'
                          : 'text-zinc-300'}
                      >
                        <td>
                          {c.profile_type}
                          {c.low_sample && (
                            <span className="ml-1 text-[8px] px-1 py-0.5 rounded-sm bg-amber-900/40 border border-amber-800 text-amber-200">
                              low-sample
                            </span>
                          )}
                        </td>
                        <td className="text-[9px]">{c.fatigue_type}</td>
                        <td>{c.recommended_market}</td>
                        <td className="text-right">{c.n}</td>
                        <td className="text-right">{c.settled}</td>
                        <td className="text-right text-emerald-300">
                          {fmtPct(c.accuracy_strict_pct)}
                        </td>
                        <td className="text-right text-violet-300">
                          {fmtPct(c.accuracy_compatible_pct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="text-[9px] text-zinc-600 pt-1 border-t border-zinc-900">
            audit-only · zero LLM · zero auto-bet · field source:{' '}
            <code className="text-zinc-500">
              {data.field_source?.profile_type || 'tt_set_profile_analyses.profile_type'}
            </code>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, testid, tone = 'zinc' }) {
  const toneCls = {
    zinc: 'text-zinc-100',
    emerald: 'text-emerald-200',
    amber: 'text-amber-200',
  }[tone] || 'text-zinc-100';
  return (
    <div
      data-testid={testid}
      className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-2"
    >
      <div className="text-[9px] text-zinc-500 tracking-wider">{label}</div>
      <div className={`${toneCls} text-[14px] font-bold`}>{value}</div>
    </div>
  );
}
